import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { getProducts, getProductsByCategory, searchProducts } from '../services/api';
import { useSeller } from '../context/SellerContext';

const PRICE_RANGES = [
  { label: '5만원 이하', value: 'under5', check: (p) => p <= 50000 },
  { label: '5만원 ~ 10만원', value: '5to10', check: (p) => p > 50000 && p <= 100000 },
  { label: '10만원 ~ 15만원', value: '10to15', check: (p) => p > 100000 && p <= 150000 },
  { label: '15만원 이상', value: 'over15', check: (p) => p > 150000 }
];

const ITEMS_PER_PAGE = 30;

export default function useProductFilters() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '전체');
  const [searchKeyword, setSearchKeyword] = useState(searchParams.get('search') || '');
  const [categorySettings, setCategorySettings] = useState(null);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestedLabel, setSuggestedLabel] = useState('');
  const { subdomain } = useSeller();

  // Pagination & Filter
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortOption, setSortOption] = useState('popular');
  const [priceFilters, setPriceFilters] = useState([]);

  const handlePriceFilterChange = (value) => {
    setPriceFilters(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    setCurrentPage(1);
  };

  const handleSortChange = (option) => {
    setSortOption(option);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo(0, 0);
    }
  };

  // URL 파라미터 동기화
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search') || '';
    if (categoryParam && categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam);
      setSearchKeyword('');
    } else if (!categoryParam && selectedCategory !== '전체' && !searchParam) {
      setSelectedCategory('전체');
    }
    if (searchParam !== searchKeyword) {
      setSearchKeyword(searchParam);
      if (searchParam) setSelectedCategory('전체');
    }
  }, [searchParams]);

  // 데이터 조회 — priceFilters 변경 시에도 재호출 (체크박스 즉시 반영)
  useEffect(() => {
    fetchProducts();
    loadCategorySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchKeyword, subdomain, sortOption, currentPage, priceFilters]);

  const loadCategorySettings = async () => {
    if (selectedCategory === '전체' || ['best', 'new', 'recommended', 'hotdeal'].includes(selectedCategory)) {
      setCategorySettings(null);
      return;
    }
    try {
      const response = await api.get(`/public/category-settings/category/${selectedCategory}`);
      setCategorySettings(response.data);
    } catch { setCategorySettings(null); }
  };

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      let sortParam = 'createdAt,desc';
      if (sortOption === 'priceLow') sortParam = 'price,asc';
      else if (sortOption === 'priceHigh') sortParam = 'price,desc';
      else if (sortOption === 'new') sortParam = 'createdAt,desc';
      else if (sortOption === 'popular') sortParam = 'salesCount,desc';

      const specialSections = ['best', 'new', 'recommended', 'hotdeal'];

      if (searchKeyword) {
        response = await searchProducts(searchKeyword, subdomain, currentPage - 1, ITEMS_PER_PAGE, sortParam);
      } else if (selectedCategory === '전체') {
        response = await getProducts(subdomain, currentPage - 1, ITEMS_PER_PAGE, sortParam);
      } else if (specialSections.includes(selectedCategory)) {
        const params = { page: currentPage - 1, size: ITEMS_PER_PAGE, sort: sortParam };
        if (subdomain) params.subdomain = subdomain;
        response = await api.get(`/public/sections/${selectedCategory}/products`, { params });
      } else {
        response = await getProductsByCategory(selectedCategory, subdomain, currentPage - 1, ITEMS_PER_PAGE, sortParam);
      }

      let fetchedProducts = [];
      if (response.data?.content) {
        fetchedProducts = response.data.content;
        if (priceFilters.length > 0) {
          fetchedProducts = fetchedProducts.filter(product =>
            priceFilters.some(filterVal => {
              const range = PRICE_RANGES.find(r => r.value === filterVal);
              return range ? range.check(product.price) : false;
            })
          );
        }
        setProducts(fetchedProducts);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalElements);
      } else {
        fetchedProducts = response.data || [];
        setProducts(fetchedProducts);
        setTotalPages(1);
        setTotalItems(fetchedProducts.length);
      }

      if (searchKeyword && fetchedProducts.length === 0) {
        await loadSuggestedProducts();
      } else {
        setSuggestedProducts([]);
        setSuggestedLabel('');
      }
    } catch {
      setError('상품을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestedProducts = async () => {
    try {
      const recParams = { page: 0, size: 6, sort: 'createdAt,desc' };
      if (subdomain) recParams.subdomain = subdomain;
      const recRes = await api.get('/public/sections/recommended/products', { params: recParams });
      const recProducts = recRes.data?.content || recRes.data || [];
      if (recProducts.length > 0) {
        setSuggestedProducts(recProducts.slice(0, 6));
        setSuggestedLabel('추천 상품');
        return;
      }
    } catch { /* ignore */ }

    try {
      const recentRes = await getProducts(subdomain, 0, 6, 'createdAt,desc');
      const recentProducts = recentRes.data?.content || recentRes.data || [];
      if (recentProducts.length > 0) {
        setSuggestedProducts(recentProducts.slice(0, 6));
        setSuggestedLabel('최근 등록 상품');
        return;
      }
    } catch { /* ignore */ }

    setSuggestedProducts([]);
    setSuggestedLabel('');
  };

  return {
    products, loading, error, fetchProducts,
    selectedCategory, setSelectedCategory,
    searchKeyword, categorySettings,
    suggestedProducts, suggestedLabel,
    currentPage, setCurrentPage, totalPages, totalItems,
    sortOption, setSortOption, priceFilters, setPriceFilters, priceRanges: PRICE_RANGES,
    handlePriceFilterChange, handleSortChange, handlePageChange,
  };
}
